import { useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useClaimIndicators } from "../../contexts/ClaimIndicatorsContext";

/**
 * Buy Page Mobile Navigation Component
 *
 * Secondary mobile navbar that appears above the main mobile navigation
 * Only shows on the Buy page (/offers) with 5 text anchor points for smooth scrolling
 * No icons - just text navigation for different sections of the buy page
 * Highlights active section based on scroll position
 */
const BuyPageMobileNavigation = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("calendar");
  const [bottomOffset, setBottomOffset] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Only show on the Buy page
  const shouldShow =
    location.pathname === "/offers" || location.pathname.startsWith("/offers");

  // Compute claim indicators (must be called unconditionally to respect Rules of Hooks)
  const { showCalendarGlow, showVipGlow } = useClaimIndicators();

  // Calculate the height of the main mobile nav dynamically
  useEffect(() => {
    if (!shouldShow) {
      setIsVisible(false);
      return;
    }

    const calculateOffset = () => {
      // Find the main mobile nav by looking for the nav with bottom-0 class
      const mainMobileNav = document.querySelector('nav[class*="bottom-0"]');
      if (mainMobileNav) {
        const height = mainMobileNav.offsetHeight;
        setBottomOffset(height);
      }
    };

    // Calculate on mount and when window resizes
    calculateOffset();
    window.addEventListener("resize", calculateOffset);

    // Trigger slide-up animation after a short delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => {
      window.removeEventListener("resize", calculateOffset);
      clearTimeout(timer);
    };
  }, [shouldShow]);

  // Smooth scroll to section with offset for header
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80; // Offset for header and spacing
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Track active section based on scroll position
  useEffect(() => {
    if (!shouldShow) return;

    const handleScroll = () => {
      const sections = [
        "calendar",
        "bonus-buys",
        "weeklys",
        "core",
        "free-vip",
      ];
      const scrollPosition = window.scrollY + 100; // Offset for better detection

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener("scroll", handleScroll);
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  // Navigation items for Buy page sections
  const navItems = [
    { id: "calendar", label: "Calendar", hasNotification: !!showCalendarGlow },
    { id: "bonus-buys", label: "Bonus Buys", hasNotification: false },
    { id: "weeklys", label: "Weeklys", hasNotification: false },
    { id: "core", label: "Core", hasNotification: false },
    { id: "free-vip", label: "Free VIP", hasNotification: !!showVipGlow },
  ];

  return (
    <nav
      className={`
        fixed left-0 right-0 z-40
        bg-card border-t border-border
        md:hidden
        transition-transform duration-300 ease-out
        ${isVisible ? "translate-y-0" : "translate-y-full"}
      `}
      style={{ bottom: `${bottomOffset}px` }}
    >
      <div className="flex justify-around items-center py-1 px-2">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => scrollToSection(item.id)}
            className={`
              relative flex-1 text-center py-1 px-1
              text-xs font-medium transition-colors duration-150
              active:scale-95 min-h-8
              ${
                activeSection === item.id
                  ? "text-primary"
                  : "text-gray-300 hover:text-white"
              }
            `}
          >
            {item.label}
            {/* Notification indicator */}
            {item.hasNotification && (
              <div className="absolute -top-0.5 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            )}
          </button>
        ))}
      </div>
    </nav>
  );
};

export default BuyPageMobileNavigation;
